import { useContext, type FC } from 'react';
import * as React from 'react';

import { Text } from '@/core/tamagoshi/Text/Text';
import { getCurrency } from '@/domains/core/settings/utils';
import { Price } from '@/domains/core/tamagoshi/Price/Price';
import {
  CurrencyType,
  FormatType,
} from '@/domains/core/tamagoshi/Price/Price.interfaces';
import { CouponContext } from '@/domains/loyalty/components/Coupon/CouponContext';
import { formatPercentageNarrowedByLocale } from '@/domains/loyalty/utils/numbers';

import { offerText } from '../../translations';

import styles from './CouponLayout.module.scss';

interface LeftContentProps {
  forceShowDecimals?: boolean;
}

const LeftContent: FC<LeftContentProps> = ({ forceShowDecimals }) => {
  const { amount, percent } = useContext(CouponContext);
  const currency = getCurrency();

  return (
    <>
      <Text tag="span" type="h3" weight="bold" className={styles.price}>
        {amount && !percent && (
          <Price
            value={amount}
            currency={CurrencyType[currency]}
            minimumFractionDigits={forceShowDecimals ? 2 : 0}
            maximumFractionDigits={forceShowDecimals ? 2 : 0}
            format={
              forceShowDecimals ? FormatType.SYMBOL_SUP : FormatType.SYMBOL
            }
          />
        )}
        {!!percent && formatPercentageNarrowedByLocale(percent)}
      </Text>

      <Text tag="span" type="body2" weight="bold" className={styles.offerText}>
        {offerText}
      </Text>
    </>
  );
};

export interface CouponLayoutProps extends LeftContentProps {
  rightContent: React.ReactNode | React.ReactNode[];
  description?: React.ReactNode | React.ReactNode[];
}

export const CouponLayout: FC<CouponLayoutProps> = ({
  rightContent,
  description = null,
  ...leftContentProps
}) => {
  const descriptionElements = React.Children.toArray(description);

  return (
    <div className={styles.couponWrapper}>
      <div className={styles.container}>
        <div className={styles.leftContainer}>
          <div className={styles.leftContent}>
            <LeftContent {...leftContentProps} />
          </div>
        </div>
        <div className={styles.rightContainer}>
          <div className={styles.rightContent}>{rightContent}</div>
        </div>
      </div>
      {descriptionElements.length > 0 && (
        <ul className={styles.descriptionList}>
          {React.Children.map(descriptionElements, (element) => (
            <li>{element}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

CouponLayout.displayName = 'CouponLayout';
